"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { ClipboardCheck } from "lucide-react";
import {
  ApiError,
  api,
  loadTokens,
  type AdminInboxResponse,
  type AdminInboxItem,
  type AdminInboxPriority,
} from "@/lib/api";

/**
 * EPIC-038 — admin notification inbox bell.
 *
 * Renders only when the authed user is an admin. Polls
 * /v1/admin/inbox every 30s; click toggles a dropdown listing items
 * grouped by priority. Each item deep-links to its source admin
 * surface and dismisses the dropdown on click.
 *
 * Distinct lucide glyph (ClipboardCheck) intentionally separates this
 * surface from the existing user-facing NotificationsBell.
 *
 * Self-gates rendering: returns null when (a) no auth tokens, (b)
 * /v1/me reports a non-admin role, or (c) the inbox endpoint 403s
 * (defensive — server-side AdminGuard is the authoritative check).
 */
const POLL_INTERVAL_MS = 30_000;

export function AdminInboxBell() {
  const [authed, setAuthed] = useState<boolean | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [data, setData] = useState<AdminInboxResponse | null>(null);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAuthed(loadTokens() !== null);
  }, []);

  useEffect(() => {
    if (authed !== true) return;
    let cancelled = false;
    void api
      .me()
      .then((profile) => {
        if (!cancelled) setIsAdmin(profile.role === "admin");
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authed]);

  useEffect(() => {
    if (isAdmin !== true) return;
    let cancelled = false;
    async function load() {
      try {
        const res = await api.getAdminInbox();
        if (!cancelled) setData(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          // Server says not-admin; suppress the bell.
          setIsAdmin(false);
          return;
        }
        // Transient errors — keep the last good data; the next poll
        // tick retries.
      }
    }
    void load();
    const handle = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
    };
  }, [isAdmin]);

  // Click-outside-to-close: capture clicks not inside the dropdown
  // wrapper and dismiss.
  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        dropdownRef.current &&
        e.target instanceof Node &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  if (isAdmin !== true) return null;

  const total = data?.total ?? 0;
  const badge = total > 99 ? "99+" : total > 0 ? String(total) : null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text2)] transition-colors hover:border-[var(--color-border2)]"
        aria-label="Admin inbox"
        aria-expanded={open}
      >
        <ClipboardCheck size={16} strokeWidth={1.75} aria-hidden="true" />
        {badge !== null && (
          <span
            className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold text-white"
            style={{ background: "var(--color-no)" }}
            aria-hidden="true"
          >
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Admin inbox"
          className="absolute right-0 top-[calc(100%+6px)] z-50 max-h-[70vh] w-[360px] overflow-y-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg"
          style={{ boxShadow: "0 6px 24px rgba(0,0,0,0.18)" }}
        >
          <div className="flex items-baseline justify-between border-b border-[var(--color-border)] px-3 py-2">
            <h3 className="text-[12px] font-bold uppercase tracking-[0.6px] text-[var(--color-text3)]">
              Admin inbox
            </h3>
            <span className="text-[10px] text-[var(--color-text3)]">
              {total} open
            </span>
          </div>
          {data === null ? (
            <p className="px-3 py-4 text-[12px] text-[var(--color-text3)]">
              Loading…
            </p>
          ) : data.items.length === 0 ? (
            <p className="px-3 py-6 text-center text-[12px] text-[var(--color-text3)]">
              All clear. No pending tasks.
            </p>
          ) : (
            <>
              <ul className="divide-y divide-[var(--color-border)]">
                {data.items.map((item) => (
                  <InboxRow
                    key={item.id}
                    item={item}
                    onClick={() => setOpen(false)}
                  />
                ))}
              </ul>
              {/* EPIC-038 follow-up — link to the full overflow page.
                  Always rendered when items exist so admins have a
                  consistent way to navigate to the un-capped list,
                  and the truncation notice surfaces inline when the
                  50-cap is hit. */}
              <Link
                href="/admin/inbox"
                onClick={() => setOpen(false)}
                className="flex items-center justify-between border-t border-[var(--color-border)] px-3 py-2 text-[11px] font-semibold text-[var(--color-text2)] transition-colors hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
              >
                <span>
                  {data.truncated
                    ? `Showing first 50 of ${total}`
                    : "View all"}
                </span>
                <span aria-hidden="true">→</span>
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function InboxRow({
  item,
  onClick,
}: {
  item: AdminInboxItem;
  onClick: () => void;
}) {
  return (
    <li>
      <Link
        href={item.href}
        onClick={onClick}
        className="block px-3 py-2.5 transition-colors hover:bg-[var(--color-bg)]"
      >
        <div className="flex items-baseline gap-2">
          <span
            aria-hidden="true"
            className="block h-2 w-2 flex-shrink-0 rounded-full"
            style={{ background: priorityColor(item.priority) }}
            title={`${item.priority.toUpperCase()} priority`}
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-semibold text-[var(--color-text)]">
              {item.title}
            </p>
            <p className="mt-0.5 truncate text-[10px] text-[var(--color-text3)]">
              {sourceLabel(item.source)} · {item.context}
            </p>
          </div>
        </div>
      </Link>
    </li>
  );
}

function priorityColor(p: AdminInboxPriority): string {
  switch (p) {
    case "critical":
      return "var(--color-no)";
    case "high":
      return "var(--color-status-warning, #c89b1f)";
    case "medium":
      return "var(--color-brand)";
    case "low":
    default:
      return "var(--color-text3)";
  }
}

function sourceLabel(s: AdminInboxItem["source"]): string {
  switch (s) {
    case "resolution":
      return "Resolution";
    case "dispute":
      return "Dispute";
    case "ticket":
      return "Ticket";
    case "pi_application":
      return "PI application";
    case "copy_health":
      return "Copy health";
    case "bot_report":
      return "Bot report";
    case "account_deletion":
      return "Account deletion";
    default:
      return s;
  }
}
